import React, { useEffect, useState } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ReactComponent as DemandForecasting } from "../../icons/dashboard/demandForecasting.svg";
import { ReactComponent as JvvnlIcon } from "../../icons/jvvnl.svg";
import { getSessionValidity } from "../../Rest_api/RestApi";
import { COLOR_CODES, isMobile } from "../../common";
import { ReactComponent as AnalyticsIcon } from "../../icons/analytics.svg";
import { ReactComponent as AnalyticsSelectedIcon } from "../../icons/analytics-selected.svg";
import { ReactComponent as DashboardIcon } from "../../icons/dashboard.svg";
import { ReactComponent as DashboardSelectedIcon } from "../../icons/dashboard-selected.svg";
import Hamburger from "../../components/Hamburger";
import { ReactComponent as GNAiIcon } from "../../icons/gnai_logo.svg";
import { ReactComponent as thunder } from "../../icons/thunder.svg";
import { ReactComponent as thunderSeleted } from "../../icons/thunder-active.svg";
import { redirectToLogin } from "../Protected";
import { SquarePen, Zap } from "lucide-react";
import { IconTab } from "../../components/IconTab";
import { PageHeader } from "../../components/Page";
import VidyutAi from "../Dashboard/NewExchnage/Tabs/VidyutAi";
import JvvnlGlobalSearch from "../../components/Search/Jvvnl/GlobalSearch";
import { ReactComponent as BankingAalytics } from "../../icons/dashboard/bankingAnalytics.svg";

function JvvnlDashboardItem({
  title,
  Icon,
  to,
}: {
  title: string;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  to: string;
}) {
  return (
    <NavLink to={`${to}`} className="dashboard-item">
      <Icon
        width={80}
        height={33}
        style={{
          fill: COLOR_CODES.primary,
        }}
      />
      <p>{title}</p>
    </NavLink>
  );
}

export function JvvnlTitle() {
  return (
    <div className="flex flex-row justify-center items-center space-x-2 ">
      <JvvnlIcon width={"40px"} height={"60px"} />
      <h1 className="text-[#6F6D7C] text-[18px]">
        JVVNL Power Analytics Center
      </h1>
    </div>
  );
}

export default function JvvnlDashboard() {
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshSession = () => {
    setRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    localStorage.removeItem("procurement_edit_id");
    getSessionValidity().then((res) => {
      if (res.status === false) {
        redirectToLogin(window.location.href);
      }
    });
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "dashboard";
  const [selectedTab, setSelectedTab] = useState(initialTab);

  useEffect(() => {
    const tab = searchParams.get("tab") || "dashboard";
    if (tab !== selectedTab) {
      setSelectedTab(tab);
    }
  }, [searchParams, selectedTab]);

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    setSearchParams({ tab });
  };

  return (
    <div className="page-wrapper">
      <PageHeader
        leftComponent={
          <div className="flex flex-row md:gap-x-4 gap-x-2 items-center">
            <GNAiIcon width={80} />
            {!isMobile && <Hamburger />}

            {selectedTab === "vidyutai" && !isMobile && (
              <h1 className="text-[18px] text-[#6F6D7C]">Vidyut AI</h1>
            )}
            {isMobile && (
              <h1 className="text-xl font-semibold tracking-wide text-gray-800">
                Vidyut AI - JVVNL
              </h1>
            )}
          </div>
        }
        middleComponent={!isMobile ? <JvvnlTitle /> : undefined}
        rightComponent={
          <div className="md:min-w-[300px] flex justify-end">
            {selectedTab !== "vidyutai" && <JvvnlGlobalSearch />}
          </div>
        }
      />

      {selectedTab === "vidyutai" ? (
        <div className="tabContent" style={{ padding: 0 }}>
          <VidyutAi key={refreshKey} />
        </div>
      ) : (
        <div className="dashboard-item-container">
          <div className="flex flex-col justify-between h-full">
            <div className="flex flex-row flex-wrap gap-5 justify-center">
              <JvvnlDashboardItem
                Icon={DemandForecasting}
                title="Net Load & DRE Flow"
                to="jvvnl-metric"
              />
              <JvvnlDashboardItem
                Icon={DemandForecasting}
                title="Load & Generation Forecast"
                to="forecast"
              />
              <JvvnlDashboardItem
                Icon={DemandForecasting}
                title="Load Map"
                to="load-map"
              />
              <JvvnlDashboardItem
                Icon={BankingAalytics}
                title="Grid Replica"
                to="grid-replica"
              />
            </div>
            <div className="gna-footer-container mt-8">
              <div className="grid grid-cols-3 justify-between items-center gap-5 ">
                <h3 className="text-[#9f9f9f] font-medium text-[13px]">
                  GNA Energy Data and Analytics Capability Center (GDACC)
                </h3>
                <p className="gna-footer-text text-center">
                  Powered by <strong>GNA Energy</strong>
                </p>
                <strong className="gna-footer-text  text-end">
                  Beta Version
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const TabSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const tabRoutes: Record<string, string> = {
    dashboard: "/jvvnl?tab=analytics",
    analytics: "/jvvnl/jvvnl-metric",
    vidyutai: "/jvvnl/jvvnl-generation-load-balance",
  };

  const activeTab = location.pathname.includes("jvvnl-metric")
    ? "analytics"
    : location.pathname.includes("jvvnl-generation-load-balance")
      ? "vidyutai"
      : "dashboard";

  const handleTabChange = (newTab: string) => {
    navigate(tabRoutes[newTab] ?? `/jvvnl?tab=${newTab}`);
  };

  return (
    <IconTab
      tabs={[
        {
          key: "dashboard",
          icon: DashboardIcon,
          selectedIcon: DashboardSelectedIcon,
        },
        {
          key: "analytics",
          icon: thunder,
          selectedIcon: thunderSeleted,
        },
        {
          key: "vidyutai",
          icon: AnalyticsIcon,
          selectedIcon: AnalyticsSelectedIcon,
        },
      ]}
      selected={activeTab}
      onChange={handleTabChange}
    />
  );
};

export const JvvnlVidyutAiButton: React.FC = () => {
  const location = useLocation();
  const isVidyutAiOpen =
    location.pathname === "/jvvnl" &&
    new URLSearchParams(location.search).get("tab") === "vidyutai";

  if (isVidyutAiOpen) return null;

  return (
    <NavLink
      to="/jvvnl?tab=vidyutai"
      title="Vidyut AI"
      className="fixed bottom-5 right-5 z-[999] flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium text-white/80 hover:text-white transition-all duration-200"
      style={{
        background: "rgba(15, 32, 39, 0.85)",
        border: "1px solid rgba(241,147,92,0.25)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
        backdropFilter: "blur(8px)",
      }}
    >
      <Zap size={13} className="text-[#f1935c]" fill="#f1935c" />
      <span className="tracking-wide">Vidyut AI</span>
    </NavLink>
  );
};
